import { ThreadListPrimitive, ThreadListItemPrimitive } from '@assistant-ui/react';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '../../../components/confirm-dialog';

interface ThreadSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const ThreadSidebar = ({ collapsed, onToggle }: ThreadSidebarProps) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: collapsed ? 0 : 160,
        overflow: 'hidden',
        transition: 'width 200ms ease',
        borderRight: collapsed ? 0 : 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Sidebar header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
        }}
      >
        <ThreadListPrimitive.New asChild>
          <Tooltip title={t('aiChat.session.new')}>
            <IconButton
              size="small"
              aria-label={t('aiChat.session.new')}
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ThreadListPrimitive.New>
        <Tooltip title={t('aiChat.sidebar.toggle')}>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={t('aiChat.sidebar.toggle')}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Section label */}
      <Typography
        variant="caption"
        sx={{ px: 1, py: 0.5, color: 'text.secondary', fontWeight: 600 }}
      >
        {t('aiChat.sidebar.conversations')}
      </Typography>

      {/* Thread list -- scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <ThreadListPrimitive.Root>
          <ThreadListPrimitive.Items>{() => <SidebarThreadItem />}</ThreadListPrimitive.Items>
        </ThreadListPrimitive.Root>
      </Box>
    </Box>
  );
};

const SidebarThreadItem = () => {
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteRef = useRef<HTMLButtonElement>(null);

  const handleDeleteClick = useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setDeleteOpen(false);
    deleteRef.current?.click();
  }, []);

  return (
    <ThreadListItemPrimitive.Root>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          borderLeft: 3,
          borderColor: 'transparent',
          '&[data-active="true"]': {
            borderColor: 'primary.main',
          },
          '&:hover .delete-btn': {
            opacity: 1,
          },
        }}
      >
        <ThreadListItemPrimitive.Trigger asChild>
          <ButtonBase
            sx={{
              flex: 1,
              justifyContent: 'flex-start',
              px: 1,
              py: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minHeight: 36,
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ display: 'block', width: '100%', textAlign: 'left' }}
            >
              <ThreadListItemPrimitive.Title />
            </Typography>
          </ButtonBase>
        </ThreadListItemPrimitive.Trigger>

        {/* Visible delete button (shown on hover) */}
        <IconButton
          size="small"
          className="delete-btn"
          onClick={handleDeleteClick}
          aria-label={t('aiChat.session.deleteConfirm')}
          sx={{ opacity: 0, transition: 'opacity 200ms', minWidth: 32, minHeight: 32 }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>

        {/* Hidden primitive delete button -- triggered programmatically on confirm */}
        <ThreadListItemPrimitive.Delete asChild>
          <button type="button" ref={deleteRef} tabIndex={-1} style={{ display: 'none' }} />
        </ThreadListItemPrimitive.Delete>
      </Box>

      <ConfirmDialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t('aiChat.session.deleteTitle')}
        message={t('aiChat.session.deleteBody')}
        confirmText={t('aiChat.session.deleteConfirm')}
        cancelText={t('aiChat.session.deleteCancel')}
      />
    </ThreadListItemPrimitive.Root>
  );
};

export const SidebarToggleButton = ({ onToggle }: { onToggle: () => void }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('aiChat.sidebar.toggle')}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={t('aiChat.sidebar.toggle')}
        sx={{ minWidth: 44, minHeight: 44 }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};
